import type { DraftTask, ProjectBrief, Task, TaskSize } from '@dyspatch/core';
import { Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';

/**
 * Amending a job after Go (docs/JOB-LIFECYCLE.md 2.6).
 *
 * The composer, prefilled: the goal as it stands, a place for rules to add, and
 * steps to add. Steps arrive as drafts and the job shows its gate again for
 * them; the goal and rules change at once, because they are what every later
 * offer is briefed with. The vocabulary rule holds: the code says brief and
 * draft task, the copy says job and step.
 *
 * Rules are added, never edited or removed here: a rule the plan was approved
 * under stays on the record, and taking one away is a different decision than
 * this form makes (cancel and refile is still the verb for "the goal was wrong").
 */

const SIZES: readonly TaskSize[] = ['xs', 's', 'm', 'l'];

interface StepDraft {
  /** A local handle for React's key: the task key is what the person is editing. */
  id: number;
  key: string;
  title: string;
  spec: string;
  criteria: string;
  repo: string;
  size: TaskSize;
  dependsOn: string;
}

function lines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function keys(text: string): string[] {
  return text
    .split(/[,\s]+/)
    .map((key) => key.trim())
    .filter(Boolean);
}

/** The next free key in the plan's own numbering, so a person rarely has to type one. */
function nextKey(existing: readonly string[]): string {
  let n = existing.length + 1;
  const taken = new Set(existing);
  while (taken.has(`T${n}`)) n += 1;
  return `T${n}`;
}

function toDraft(step: StepDraft): DraftTask {
  return {
    key: step.key.trim(),
    title: step.title.trim(),
    spec: step.spec.trim(),
    acceptanceCriteria: lines(step.criteria),
    repo: step.repo,
    size: step.size,
    priority: 'normal',
    dependsOn: keys(step.dependsOn),
  };
}

/** Why the hub would refuse this step, in the reader's own words, or null. */
function stepProblem(step: StepDraft, known: ReadonlySet<string>): string | null {
  if (!/^[A-Za-z][A-Za-z0-9_-]{0,15}$/.test(step.key.trim())) return 'Key must look like T7.';
  if (known.has(step.key.trim())) return `The job already has a ${step.key.trim()}.`;
  if (step.title.trim().length < 3) return 'Give it a title.';
  if (step.spec.trim().length < 10) return 'The spec needs at least a sentence.';
  if (lines(step.criteria).length === 0) return 'One acceptance criterion at least.';
  return null;
}

export function AmendComposer({
  brief,
  tasks,
  onClose,
}: {
  brief: ProjectBrief;
  tasks: readonly Task[];
  onClose: () => void;
}) {
  const [goal, setGoal] = useState(brief.goal);
  const [rules, setRules] = useState('');
  const [steps, setSteps] = useState<StepDraft[]>([]);
  const [nextId, setNextId] = useState(1);
  const existingKeys = tasks.map((task) => task.key);
  const known = new Set(existingKeys);
  const addedRules = lines(rules);
  const goalChanged = goal.trim() !== brief.goal && goal.trim().length >= 10;
  const problems = steps.map((step) => stepProblem(step, known));
  const duplicateWithin = steps.some(
    (step, index) => steps.findIndex((other) => other.key.trim() === step.key.trim()) !== index,
  );
  const blocked =
    problems.some(Boolean) ||
    duplicateWithin ||
    (!goalChanged && addedRules.length === 0 && steps.length === 0);

  const amend = useHubAction(
    () =>
      api.amendBrief(brief.id, {
        goal: goalChanged ? goal.trim() : undefined,
        addedConstraints: addedRules,
        tasks: steps.map(toDraft),
      }),
    {
      onDone: onClose,
      success: steps.length
        ? `Amended. ${steps.length} added task${steps.length === 1 ? '' : 's'} wait for your approval.`
        : 'Amended.',
      inlineError: true,
    },
  );

  const addStep = () => {
    setNextId((current) => current + 1);
    setSteps((current) => [
      ...current,
      {
        id: nextId,
        key: nextKey([...existingKeys, ...current.map((step) => step.key)]),
        title: '',
        spec: '',
        criteria: '',
        repo: brief.repos[0] ?? '',
        size: 's',
        dependsOn: '',
      },
    ]);
  };
  const patchStep = (index: number, patch: Partial<StepDraft>) =>
    setSteps((current) => current.map((step, at) => (at === index ? { ...step, ...patch } : step)));

  return (
    <form
      className="bg-sunken border-t px-gutter py-3"
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onClose();
      }}
      onSubmit={(event) => {
        event.preventDefault();
        if (blocked) return;
        amend.run();
      }}
    >
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-medium">Amend this job</h4>
        <span className="text-fg-3 text-2xs">
          {brief.status === 'done' ? 'reopens the finished job' : 'the running tasks carry on'}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="ml-auto"
          onClick={onClose}
          aria-label="Discard this amendment"
        >
          <X />
        </Button>
      </div>

      <div className="mt-2 space-y-1">
        <Label htmlFor="am-goal">What should be true when it is done</Label>
        <Textarea
          id="am-goal"
          value={goal}
          onChange={(event) => setGoal(event.target.value)}
          rows={4}
          minLength={10}
          maxLength={20_000}
        />
        <p className="text-fg-3 text-2xs">
          {goalChanged
            ? 'Changed. The previous goal stays in the history below.'
            : 'Unchanged so far. Every later offer is briefed with this text.'}
        </p>
      </div>

      <div className="mt-2 space-y-1">
        <Label htmlFor="am-rules">Rules to add</Label>
        <Textarea
          id="am-rules"
          value={rules}
          onChange={(event) => setRules(event.target.value)}
          rows={2}
          placeholder={'One per line. Existing rules stay.'}
        />
      </div>

      <div className="mt-3 flex items-center gap-2">
        <span className="dy-eyebrow">Tasks to add</span>
        <Button type="button" size="xs" variant="outline" onClick={addStep}>
          <Plus aria-hidden />
          Add a task
        </Button>
        <span className="text-fg-3 text-2xs">
          They wait for your approval before anything runs.
        </span>
      </div>

      {steps.map((step, index) => (
        <fieldset key={step.id} className="bg-card mt-2 space-y-2 rounded-md border px-2.5 py-2">
          <legend className="sr-only">Added task {index + 1}</legend>
          <div className="grid gap-2 sm:grid-cols-[6rem_minmax(0,1fr)_7rem]">
            <div className="space-y-1">
              <Label htmlFor={`am-key-${index}`}>Key</Label>
              <Input
                id={`am-key-${index}`}
                value={step.key}
                onChange={(event) => patchStep(index, { key: event.target.value })}
                className="font-mono"
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`am-title-${index}`}>Title</Label>
              <Input
                id={`am-title-${index}`}
                value={step.title}
                onChange={(event) => patchStep(index, { title: event.target.value })}
                minLength={3}
                maxLength={200}
                placeholder="Add the notes table"
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`am-size-${index}`}>Size</Label>
              <select
                id={`am-size-${index}`}
                value={step.size}
                onChange={(event) => patchStep(index, { size: event.target.value as TaskSize })}
                className="border-line-2 bg-background h-8 w-full rounded-md border px-2 text-sm"
              >
                {SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor={`am-repo-${index}`}>Repo</Label>
              <select
                id={`am-repo-${index}`}
                value={step.repo}
                onChange={(event) => patchStep(index, { repo: event.target.value })}
                className="border-line-2 bg-background h-8 w-full rounded-md border px-2 text-sm"
              >
                {brief.repos.map((repo) => (
                  <option key={repo} value={repo}>
                    {repo}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`am-after-${index}`}>After</Label>
              <Input
                id={`am-after-${index}`}
                value={step.dependsOn}
                onChange={(event) => patchStep(index, { dependsOn: event.target.value })}
                placeholder={existingKeys[0] ? `${existingKeys[0]}, ...` : 'T1, T2'}
                className="font-mono"
              />
              <p className="text-fg-3 text-2xs">Keys this task waits on; existing tasks count.</p>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor={`am-spec-${index}`}>Spec</Label>
            <Textarea
              id={`am-spec-${index}`}
              value={step.spec}
              onChange={(event) => patchStep(index, { spec: event.target.value })}
              rows={4}
              minLength={10}
              placeholder="Everything a headless agent needs with no further context."
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`am-criteria-${index}`}>Acceptance criteria</Label>
            <Textarea
              id={`am-criteria-${index}`}
              value={step.criteria}
              onChange={(event) => patchStep(index, { criteria: event.target.value })}
              rows={2}
              placeholder="One per line."
              required
            />
          </div>
          <div className="flex items-center gap-2">
            {problems[index] ? (
              <span className="text-bad text-2xs" role="alert">
                {problems[index]}
              </span>
            ) : null}
            <Button
              type="button"
              size="xs"
              variant="ghost"
              className="ml-auto"
              onClick={() => setSteps((current) => current.filter((_, at) => at !== index))}
            >
              <Trash2 aria-hidden />
              Remove
            </Button>
          </div>
        </fieldset>
      ))}
      {duplicateWithin ? (
        <p className="text-bad mt-1.5 text-2xs" role="alert">
          Two added tasks share a key.
        </p>
      ) : null}

      {amend.error ? (
        <p className="text-bad mt-2 text-xs" role="alert">
          {amend.error}
        </p>
      ) : null}

      <div className="mt-3 flex items-center gap-1.5">
        <Button type="submit" size="sm" disabled={amend.busy || blocked} className="max-sm:h-9">
          {amend.busy ? 'Amending…' : 'Amend job'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onClose} className="max-sm:h-9">
          Cancel
        </Button>
        {!goalChanged && addedRules.length === 0 && steps.length === 0 ? (
          <span className="text-fg-3 text-2xs">Change the goal, add a rule or add a task.</span>
        ) : null}
      </div>
    </form>
  );
}
